import { ValidationFunction } from "not-valid";
import * as moment from "moment";

import { QuestionConfig } from "./questionnaireConfig/models";
import { Point } from "./components/questions/imageAnnotatorQuestion/models";

export interface QuestionnairesState {
    [key: string]: QuestionnaireState;
}

export enum QuestionnaireTypes {
    ClinicalNotes = "ClinicalNotes",
    Report = "Report",
    Other = "Other"
}

export type QuestionnaireType = QuestionnaireTypes.ClinicalNotes | QuestionnaireTypes.Report | QuestionnaireTypes.Other;

export interface QuestionnaireState {
    sections: Section[];
    eTag?: string | null; // Null if the questionnaire has never been saved
    referralSubmittedDate?: moment.Moment;
    isDefaultCustomerType?: boolean;
    customerType?: string;
    reportChangesBlocked?: boolean;
    releaseReportComments?: string;
    approvalState?: string;
    isConsentWithdrawn?: boolean;
}

export interface FileAnswer {
    blobId: string;
    fileName: string;
    ohCaseDocumentId?: string;
}

export interface OhCaseDocumentFile {
    blobId: string;
    ohCaseDocumentId: string;
}

export interface AnsweredQuestion {
    /**
     * @deprecated Use `AnsweredQuestionWithId` instead of `AnsweredQuestion` if you are expecting an id to be present
     * If you are creating an object with an id property, consider if the id prop is really needed or used, there is a good chance that it's not used
     * This prop will be removed from this type as soon as all usages have been updated
     */
    id?: string;
    question: string;
    sectionId?: string;
    answer: QuestionAnswer;
    userFriendlyAnswer?: QuestionAnswer; // Added so that dropdown questions/answers can display user friendly value
    isValid?: boolean;
    isHidden?: boolean;
    isRemovedByUser?: boolean;
    isValidating?: boolean;
}

export type AnsweredQuestionWithId = Omit<AnsweredQuestion, "id"> & { id: string };

export type QuestionAnswer = string | FileAnswer | string[] | FileAnswer[] | Point[] | number | null | undefined;

export interface WarningProps {
    validators: ValidationFunction<any>[];
}

export interface QuestionProps extends Partial<QuestionInjectedProps> {
    questionConfig: QuestionConfig;
    questionClassName?: string;
    mandatory?: boolean;
    validators?: ValidationFunction<any>[];
    readonly: boolean;
    hidden?: boolean;
    outdated?: OutdatedQuestionProps;
    removable?: RemoveQuestionProps;
    isRemovedByUser?: boolean;
    popupContent?: React.ReactNode;
    inline?: boolean;
    hideDivider?: boolean;
    validationDependencies?: any[]; // If any of these change, the question should revalidate
    onAnswerChange?: (answer: QuestionAnswer) => void;
    answerHighlightCriteria?: (answer: QuestionAnswer) => boolean;

    warning?: WarningProps;
    shouldShowError?: boolean;
    initialAnswer?: QuestionAnswer;
    preserveInitialQuestionText?: boolean;
}

export interface QuestionInjectedProps {
    /**
     * @deprecated When reading this value prefer `const { answer } = useQuestionnaireSectionForQuestion(props);` instead.
     * When writing to this value prefer the new `initialAnswer` prop instead
     */
    answer: QuestionAnswer;
    /**
     * @deprecated When reading this value prefer `const { showError } = useQuestionnaireSectionForQuestion(props);` instead.
     * When writing to this value prefer the new `shouldShowError` prop instead
     */
    showError: boolean;
    /**
     * @deprecated When reading this value prefer `const { extraContent } = useQuestionnaireSectionForQuestion(props);` instead
     * When writing to this value prefer the `extraQuestionContent` prop on the questionnaire container or the `includeTextSnippetSelector` prop instead
     */
    extraContent: React.ReactNode;
    /**
     * @deprecated Prefer `const { updateAnswer } = useQuestionnaireSectionForQuestion(props);` instead
     */
    updateQuestion: (question: AnsweredQuestion) => void;
    /**
     * @deprecated Prefer calling `updateAnswer` returned by `useQuestionnaireSectionForQuestion(props);` after awaiting any validation instead
     */
    validateQuestion: (validatedQuestion: Promise<AnsweredQuestion>) => void;
}

export type QuestionMap = { [id: string]: AnsweredQuestionWithId };

export type OutdatedQuestionProps = {
    questionnaireKey: string;
    sectionId: string;
    isOutdated: boolean;
}

export type RemoveQuestionProps = {
    questionnaireKey: string;
    sectionId: string;
}

export type QuestionnaireQuestion = {
    questionnaireKey: string;
    sectionId: string;
    questionId: string;
}

export type RemoveQuestionWrapperProps = QuestionnaireQuestion & {
    isRemovedByUser: boolean | undefined;
}

export type OutdatedQuestionWrapperProps = QuestionnaireQuestion & {
    isOutdated: boolean;
}

export interface Section {
    id: string;
    index?: number;
    questions: QuestionMap;
    active: boolean;
    isValid: boolean;
    isComplete: boolean;
    title: string;
    neverHide: boolean;
}

export interface QuestionnaireData {
    questions: AnsweredQuestionWithId[];
}

export interface Questionnaire {
    id: string;
    data: QuestionnaireData;
    eTag?: string;
    isDefaultCustomerType?: boolean;
    customerType?: string;
}

export const initialState: QuestionnairesState = { };

export interface OrgLevel {
    id: string;
    name: string;
    parentId: string;
}

export interface DropdownOption {
    text: string;
    value: string;
    old?: boolean;
    order?: number;
}
